import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * MetaManager — sets page-level SEO & social metadata on route change.
 * Initial-HTML meta (homepage) lives in public/index.html for crawlers.
 * This updates title, description, robots, canonical, Open Graph, Twitter
 * and a per-route JSON-LD WebPage block when the user navigates client-side.
 */

const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://www.nymphe.co';
const OG_IMAGE = `${SITE_URL}/og-nymphe-share-v4.png`;
const OG_IMAGE_ALT = 'NYMPHÉ — Intention-Based Dating & Private Connection';
const SITE_NAME = 'NYMPHÉ';
const PRIVATE_ROUTES = ['/access', '/invite', '/app-preview', '/admin'];
const PRIVATE_EXACT_ROUTES = new Set(['/private', '/canva-brand-kit']);
/* Routes that exist in the app but are not ready for indexing — thin
   content, stub pages, or pages without dedicated SEO meta. Marked
   `noindex, follow` so they don't dilute the site with homepage-duplicate
   titles/descriptions (was the root cause of "Crawled — not indexed" in
   Search Console) while still allowing link equity to flow out. */
const NOINDEX_ROUTES = new Set(['/premium', '/report', '/mission', '/press']);

const ROUTE_META = {
  '/': {
    title: 'NYMPHÉ | Intention-Based Dating & Private Connection',
    description:
      'NYMPHÉ is a curated connection platform for people who value chemistry, privacy, and intention over swipe culture.',
  },
  '/the-app': {
    title: 'The App | NYMPHÉ',
    description:
      'Explore how NYMPHÉ works — from curated discovery and private profiles to trust-led connection tools designed for intentional dating.',
    schemaType: 'WebPage',
  },
  '/membership': {
    title: 'Membership | NYMPHÉ',
    description:
      'Discover NYMPHÉ membership tiers, premium features, and private connection tools designed for a more intentional dating experience.',
    schemaType: 'WebPage',
  },
  '/safety': {
    title: 'Safety & Trust | NYMPHÉ',
    description:
      'NYMPHÉ is built around privacy, verification, boundaries, and safer connection tools for a more trusted dating experience.',
    schemaType: 'WebPage',
  },
  '/access': {
    title: 'Request Access | NYMPHÉ',
    description:
      'Request early access to NYMPHÉ and join a curated community built for private, intentional connection.',
  },
  '/privacy': {
    title: 'Privacy Policy | NYMPHÉ',
    description:
      'Read the NYMPHÉ privacy policy and learn how user information, privacy, and platform data are handled.',
    schemaType: 'WebPage',
  },
  '/terms': {
    title: 'Terms of Service | NYMPHÉ',
    description:
      'Read the NYMPHÉ terms of service, including platform usage, membership terms, and user responsibilities.',
    schemaType: 'WebPage',
  },
  '/faq': {
    title: 'FAQ | NYMPHÉ',
    description:
      'Answers on access, privacy, verification, and how NYMPHÉ is designed for trans women, femmes, and respectful admirers.',
  },
  '/brand-kit': {
    title: 'NYMPHÉ Brand Kit | Official Visual Identity & Canva Assets',
    description:
      'Explore the official NYMPHÉ brand kit, including logo usage, colour palette, typography, voice, visual direction, and Canva Business assets.',
    schemaType: 'WebPage',
  },
  '/canva-brand-kit': {
    title: 'NYMPHÉ Canva Brand Kit',
    description:
      'Private access portal for approved NYMPHÉ Canva Business brand assets and templates.',
  },
  '/verified-encounters': {
    title: 'Verified Encounters | NYMPHÉ',
    description:
      'Learn how NYMPHÉ Verified Encounters support trust, discretion, authenticity, and safer private connections.',
    schemaType: 'WebPage',
  },
  '/contact': {
    title: 'Contact | NYMPHÉ',
    description: 'Reach NYMPHÉ directly. A private, intentional space for trans women, femmes, and admirers.',
  },
  '/private-dating-app': {
    title: 'Private Dating App | NYMPHÉ',
    description:
      'NYMPHÉ is a private dating app for adults who would rather be discreet than discoverable — invitation-led, off the public grid, designed around real privacy.',
    schemaType: 'WebPage',
  },
  '/dating-without-swiping': {
    title: 'Dating Without Swiping | NYMPHÉ',
    description:
      'A dating platform with no swipes, no streaks, no gamified feed. NYMPHÉ replaces the swipe with curated discovery and mutual interest signals.',
    schemaType: 'WebPage',
  },
  '/intentional-dating': {
    title: 'Intentional Dating | NYMPHÉ',
    description:
      'NYMPHÉ is built for intentional dating — clarity of purpose, mutual signals, and conversation that begins only when both people opt in.',
    schemaType: 'WebPage',
  },
  '/exclusive-dating-platform': {
    title: 'Exclusive Dating Platform | NYMPHÉ',
    description:
      'A curated, invitation-led dating platform for adults who prefer a quieter, more considered space. NYMPHÉ holds the room.',
    schemaType: 'WebPage',
  },
  '/dating-app-for-trans-women': {
    title: 'Dating App for Trans Women | NYMPHÉ',
    description:
      'A dating app made with and for trans women, femmes, and respectful admirers. Built on community, not labels — privacy and dignity by design.',
    schemaType: 'WebPage',
  },
};

/* Pages that get an isPartOf link to the NYMPHÉ website schema. /privacy
   and /terms get a WebPage schema only (no isPartOf). */
const ROUTES_WITH_IS_PART_OF = new Set([
  '/the-app',
  '/membership',
  '/safety',
  '/private-dating-app',
  '/dating-without-swiping',
  '/intentional-dating',
  '/exclusive-dating-platform',
  '/dating-app-for-trans-women',
]);
const PAGE_SCHEMA_ELEMENT_ID = 'mm-jsonld-page';

const setMeta = (selector, attr, value) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    const m = selector.match(/\[(.+?)="(.+?)"\]/);
    if (m) el.setAttribute(m[1], m[2]);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

const setLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const setPageSchema = (json) => {
  let el = document.getElementById(PAGE_SCHEMA_ELEMENT_ID);
  if (!json) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = PAGE_SCHEMA_ELEMENT_ID;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(json);
};

const MetaManager = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const isPrivate =
      PRIVATE_EXACT_ROUTES.has(pathname) ||
      PRIVATE_ROUTES.some((r) => pathname === r || pathname.startsWith(`${r}/`));
    const isNoindex = isPrivate || NOINDEX_ROUTES.has(pathname);
    const meta = ROUTE_META[pathname] || ROUTE_META['/'];
    const url = `${SITE_URL}${pathname === '/' ? '/' : pathname}`;

    if (meta.title) document.title = meta.title;
    setMeta(
      'meta[name="robots"]',
      'content',
      isPrivate ? 'noindex, nofollow' : (isNoindex ? 'noindex, follow' : 'index, follow')
    );
    setMeta('meta[name="description"]', 'content', meta.description);
    setLink('canonical', url);

    setMeta('meta[property="og:type"]', 'content', 'website');
    setMeta('meta[property="og:site_name"]', 'content', SITE_NAME);
    setMeta('meta[property="og:title"]', 'content', meta.title);
    setMeta('meta[property="og:description"]', 'content', meta.description);
    setMeta('meta[property="og:url"]', 'content', url);
    setMeta('meta[property="og:image"]', 'content', OG_IMAGE);
    setMeta('meta[property="og:image:width"]', 'content', '1200');
    setMeta('meta[property="og:image:height"]', 'content', '630');
    setMeta('meta[property="og:image:alt"]', 'content', OG_IMAGE_ALT);
    setMeta('meta[property="og:locale"]', 'content', 'en_AU');

    setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', 'content', meta.title);
    setMeta('meta[name="twitter:description"]', 'content', meta.description);
    setMeta('meta[name="twitter:image"]', 'content', OG_IMAGE);
    setMeta('meta[name="twitter:image:alt"]', 'content', OG_IMAGE_ALT);

    /* Per-route WebPage JSON-LD. Homepage already carries WebSite +
       Organization schema in index.html, so we skip injection there.
       Noindex routes also skip schema to avoid duplicate content signals. */
    if (pathname === '/' || isPrivate || isNoindex || meta.schemaType !== 'WebPage') {
      setPageSchema(null);
      return;
    }
    const pageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: meta.title,
      url,
      description: meta.description,
      inLanguage: 'en-AU',
    };
    if (ROUTES_WITH_IS_PART_OF.has(pathname)) {
      pageSchema.isPartOf = {
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        name: SITE_NAME,
        url: `${SITE_URL}/`,
      };
    }
    setPageSchema(pageSchema);
  }, [pathname]);

  return null;
};

export default MetaManager;
